import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useCreateInvite,
  useDeleteWorkspace,
  useInvites,
  useMembers,
  useMe,
  useRemoveMember,
  useRenameWorkspace,
  useRevokeInvite,
} from '../api/hooks.js';
import { setWorkspaceId, useWorkspaceId } from '../workspace.js';
import { ApiError } from '../api/client.js';
import { formatDate } from '../lib/format.js';

export default function WorkspaceSettings() {
  const workspaceId = useWorkspaceId();
  const me = useMe();
  const navigate = useNavigate();
  const current = me.data?.workspaces.find((w) => w.id === workspaceId);
  const role = current?.role;
  const canManage = role === 'owner' || role === 'admin';
  const isOwner = role === 'owner';

  const members = useMembers();
  const invites = useInvites();
  const removeMember = useRemoveMember();
  const revokeInvite = useRevokeInvite();
  const createInvite = useCreateInvite();
  const rename = useRenameWorkspace();
  const del = useDeleteWorkspace();

  const [email, setEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'member'>('member');
  const [error, setError] = useState<string | null>(null);

  const [nameDraft, setNameDraft] = useState(current?.name ?? '');
  const [renameError, setRenameError] = useState<string | null>(null);
  const [renameOk, setRenameOk] = useState(false);

  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Keep the rename field in sync if the user switches workspaces mid-edit.
  useEffect(() => {
    setNameDraft(current?.name ?? '');
    setRenameOk(false);
    setRenameError(null);
  }, [current?.id]);

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Workspace</h1>
        <p className="mt-1 text-sm text-slate-500">Manage members and pending invites.</p>
      </div>

      <section className="card p-5">
        <h2 className="text-sm font-semibold text-slate-700">Members</h2>
        <ul className="mt-3 divide-y divide-slate-100">
          {(members.data ?? []).map((m) => (
            <li key={m.id} className="flex items-center justify-between py-3">
              <div>
                <p className="text-sm font-medium text-slate-900">{m.name ?? m.email}</p>
                <p className="text-xs text-slate-500">{m.email} · joined {formatDate(m.joined_at)}</p>
              </div>
              <div className="flex items-center gap-3">
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs uppercase tracking-wide text-slate-600">
                  {m.role}
                </span>
                {role === 'owner' && m.role !== 'owner' && (
                  <button
                    className="text-xs text-red-600 hover:underline"
                    onClick={() => {
                      if (confirm(`Remove ${m.email} from workspace?`)) {
                        removeMember.mutate(m.id);
                      }
                    }}
                  >
                    Remove
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      </section>

      {canManage && (
        <section className="card p-5">
          <h2 className="text-sm font-semibold text-slate-700">Invite a teammate</h2>
          <form
            className="mt-3 flex flex-wrap items-end gap-2"
            onSubmit={async (e) => {
              e.preventDefault();
              setError(null);
              try {
                await createInvite.mutateAsync({ email, role: inviteRole });
                setEmail('');
              } catch (err) {
                if (err instanceof ApiError) {
                  setError(((err.payload as { error?: string })?.error ?? 'request_failed').replace(/_/g, ' '));
                } else {
                  setError('Request failed');
                }
              }
            }}
          >
            <div className="flex-1 min-w-[200px]">
              <label className="label">Email</label>
              <input
                type="email"
                required
                className="input mt-1"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <label className="label">Role</label>
              <select
                className="input mt-1"
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value as 'admin' | 'member')}
              >
                <option value="member">Member</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <button type="submit" disabled={createInvite.isPending} className="btn-primary">
              {createInvite.isPending ? 'Sending…' : 'Send invite'}
            </button>
          </form>
          {error && (
            <p className="mt-2 rounded-md bg-red-50 p-2 text-sm text-red-700">{error}</p>
          )}
          <p className="mt-3 text-xs text-slate-500">
            Local dev: the magic invite link is logged to the Worker console.
          </p>
        </section>
      )}

      {isOwner && (
        <section className="card p-5">
          <h2 className="text-sm font-semibold text-slate-700">Workspace name</h2>
          <form
            className="mt-3 flex flex-wrap items-end gap-2"
            onSubmit={async (e) => {
              e.preventDefault();
              setRenameError(null);
              setRenameOk(false);
              try {
                await rename.mutateAsync({ name: nameDraft.trim() });
                setRenameOk(true);
              } catch (err) {
                setRenameError(
                  err instanceof ApiError
                    ? ((err.payload as { error?: string })?.error ?? 'request_failed').replace(/_/g, ' ')
                    : 'Rename failed',
                );
              }
            }}
          >
            <div className="flex-1 min-w-[200px]">
              <label className="label">Name</label>
              <input
                type="text"
                required
                maxLength={80}
                className="input mt-1"
                value={nameDraft}
                onChange={(e) => { setNameDraft(e.target.value); setRenameOk(false); }}
              />
            </div>
            <button
              type="submit"
              disabled={rename.isPending || nameDraft.trim() === (current?.name ?? '')}
              className="btn-primary"
            >
              {rename.isPending ? 'Saving…' : 'Save'}
            </button>
          </form>
          {renameError && (
            <p className="mt-2 rounded-md bg-red-50 p-2 text-sm text-red-700">{renameError}</p>
          )}
          {renameOk && (
            <p className="mt-2 rounded-md bg-emerald-50 p-2 text-sm text-emerald-700">Saved.</p>
          )}
        </section>
      )}

      <section className="card p-5">
        <h2 className="text-sm font-semibold text-slate-700">Pending invites</h2>
        <ul className="mt-3 divide-y divide-slate-100">
          {(invites.data ?? []).filter((i) => !i.accepted_at).length === 0 ? (
            <li className="py-3 text-sm text-slate-500">No pending invites.</li>
          ) : (
            (invites.data ?? [])
              .filter((i) => !i.accepted_at)
              .map((i) => (
                <li key={i.id} className="flex items-center justify-between py-3">
                  <div>
                    <p className="text-sm font-medium text-slate-900">{i.email}</p>
                    <p className="text-xs text-slate-500">
                      {i.role} · expires {formatDate(i.expires_at)}
                    </p>
                  </div>
                  {canManage && (
                    <button
                      className="text-xs text-red-600 hover:underline"
                      onClick={() => revokeInvite.mutate(i.id)}
                    >
                      Revoke
                    </button>
                  )}
                </li>
              ))
          )}
        </ul>
      </section>

      {isOwner && (
        <section className="card border-red-200 p-5">
          <h2 className="text-sm font-semibold text-red-700">Delete workspace</h2>
          <p className="mt-1 text-sm text-slate-600">
            Permanently delete this workspace, its links, clicks, invites, and
            subscription. Past redemptions of signup codes stay intact (they're
            historical). This can't be undone.
          </p>
          <form
            className="mt-3 flex flex-wrap items-end gap-2"
            onSubmit={async (e) => {
              e.preventDefault();
              setDeleteError(null);
              try {
                await del.mutateAsync({ confirm_name: deleteConfirm.trim() });
                // Drop the cached workspace selection; AppLayout will pick the
                // next available one from /me (or render the empty state if
                // this was the user's only workspace).
                setWorkspaceId(null);
                navigate('/app', { replace: true });
              } catch (err) {
                if (err instanceof ApiError) {
                  const code = (err.payload as { error?: string })?.error ?? 'request_failed';
                  setDeleteError(
                    code === 'confirmation_mismatch'
                      ? `Type the workspace name exactly (\"${current?.name}\") to confirm.`
                      : code.replace(/_/g, ' '),
                  );
                } else {
                  setDeleteError('Delete failed');
                }
              }
            }}
          >
            <div className="flex-1 min-w-[240px]">
              <label className="label">
                Type <code className="rounded bg-slate-100 px-1">{current?.name}</code> to confirm
              </label>
              <input
                type="text"
                required
                className="input mt-1"
                value={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.value)}
              />
            </div>
            <button
              type="submit"
              disabled={del.isPending || deleteConfirm.trim() !== current?.name}
              className="btn-danger"
            >
              {del.isPending ? 'Deleting…' : 'Delete workspace'}
            </button>
          </form>
          {deleteError && (
            <p className="mt-2 rounded-md bg-red-50 p-2 text-sm text-red-700">{deleteError}</p>
          )}
        </section>
      )}
    </div>
  );
}
